import produce from "immer";
import { clamp } from "lodash";

import { assertIsDefined } from "../assertions.js";
import { asciiCompare } from "../fractionalIndexing.js";

import {
  GRID_ROW_HORIZONTAL_PADDING,
  NUM_ROW_SEGMENTS,
} from "./gridConstants.js";
import { GridColumn } from "./gridTypes.js";

// A list of columns that is guaranteed to be in order and non-overlapping
type SortedColumns = GridColumn[] & { __type: "sortedColumns" };

const columnOrderComparator = (a: GridColumn, b: GridColumn): number => {
  const order = a.start - b.start;
  return order !== 0 ? order : asciiCompare(a.id, b.id);
};

/**
 * For a given list of grid columns for a single row,
 * sorts them by their start positions,
 * and though it shouldn't be necessary, removes any erroneously overlapping elements
 * in case it somehow happened.
 */
export const getSortedAndValidatedColumns = (
  columns: readonly GridColumn[],
): SortedColumns => {
  // Sort columns by their `start` position
  const sortedColumns = [...columns].sort(columnOrderComparator);

  // Get rid of any column that overlaps with the column before it
  // NOTE(grid): Do we want to do error reporting or handling if columns get dropped?
  const finalColumns = sortedColumns.filter((col, i) => {
    const prevCol = sortedColumns[i - 1];
    return prevCol == null || prevCol.end <= col.start;
  });

  return finalColumns as SortedColumns;
};

export interface GaplessGridColumnWhitespace {
  type: "WHITESPACE";
  column: {
    start: number;
    end: number;
  };
}
export interface GaplessGridColumnContent {
  type: "CONTENT";
  column: GridColumn;
}
export type GaplessGridColumn =
  | GaplessGridColumnWhitespace
  | GaplessGridColumnContent;

/**
 * Takes a list of sorted columns and returns a new list of columns where
 * any previous empty space is filled by a fake "column" with a `type` of "WHITESPACE".
 */
export const getGaplessColumns = (
  sortedColumns: SortedColumns,
): GaplessGridColumn[] => {
  const gaplessColumns: GaplessGridColumn[] = [];
  let lastEnd = 0;
  for (const col of sortedColumns) {
    if (col.start !== lastEnd) {
      gaplessColumns.push({
        type: "WHITESPACE",
        column: {
          start: lastEnd,
          end: col.start,
        },
      });
    }

    gaplessColumns.push({
      type: "CONTENT",
      column: col,
    });
    lastEnd = col.end;
  }

  if (lastEnd !== NUM_ROW_SEGMENTS) {
    gaplessColumns.push({
      type: "WHITESPACE",
      column: {
        start: lastEnd,
        end: NUM_ROW_SEGMENTS,
      },
    });
  }

  return gaplessColumns;
};

/**
 * @rowRect A bounding client rect of a row in the grid
 * @xCoord x-coordinate of a point using the same client coordinate system
 * @numSnapPoints Of the total `NUM_ROW_SEGMENTS`, return a smaller subset of those points
 * in order to make a user interaction more quantized.
 * @returns Which row segment `xCoord` is overtop of - always a number between 0 and NUM_ROW_SEGMENTS
 */
export const getRowSegmentFromClientCoords = (
  rowRect: DOMRectReadOnly,
  xCoord: number,
  numSnapPoints: number,
): number => {
  const trueLeft = rowRect.left + GRID_ROW_HORIZONTAL_PADDING;
  const trueWidth = rowRect.width - 2 * GRID_ROW_HORIZONTAL_PADDING;
  const relativeXCoord = clamp(xCoord - trueLeft, 0, trueWidth);
  const widthRatio = relativeXCoord / trueWidth;

  // Get closest snap point based on cursor position
  const snapPoint = Math.round(widthRatio * numSnapPoints);

  // Return final value on the full 0 to `NUM_ROW_SEGMENTS` scale.
  return Math.round(snapPoint * (NUM_ROW_SEGMENTS / numSnapPoints));
};

/**
 * Given a list of gapless columns for a row,
 * returns which column `rowSegment` would be in.
 */
export const getGaplessColumnFromSegment = (
  columns: readonly GaplessGridColumn[],
  rowSegment: number,
): GaplessGridColumn | undefined =>
  columns.find(
    (col) => col.column.start <= rowSegment && rowSegment < col.column.end,
  );

/**
 * Returns if a row is filled by equally-sized columns without gaps.
 * A little bit of tolerance is built in so rows that are very close to this but not exact
 * also return true.
 */
export const isApproximatelyEquallySizedDenseRow = (
  columns: readonly GaplessGridColumn[],
): boolean => {
  assertIsDefined(columns[0]);

  const firstColSize = columns[0].column.end - columns[0].column.start;
  return columns.every((c) => {
    const colSize = c.column.end - c.column.start;

    // In general, any whitespace/gap means that we don't have a dense row.
    // But if the whitespace is really tiny, we'll just ignore it since it's not really noticable to a user
    if (c.type === "WHITESPACE") {
      return colSize <= 2;
    }

    // For any real column, we just make sure that its size is the same as the first real column.
    // We add a little bit of extra tolerance here as well just in case.
    return firstColSize - 2 <= colSize && colSize <= firstColSize + 2;
  });
};

/**
 * Returns if a row is exactly filled by equally-sized columns without gaps.
 */
export const isExactlyEquallySizedDenseRow = (
  columns: readonly GaplessGridColumn[],
): boolean => {
  // Similar logic to `makeEqualDenseRow` below, keep in sync
  const numColumns = columns.filter((c) => c.type === "CONTENT").length;
  const columnSize = NUM_ROW_SEGMENTS / numColumns;
  return columns.every((col, i) => {
    return (
      col.type === "CONTENT" &&
      col.column.start === Math.round(columnSize * i) &&
      col.column.end === Math.round(columnSize * (i + 1))
    );
  });
};

/** Resizes the existing columns in a row so that they fill the row and are equally-sized */
export const makeEqualDenseRow = (
  gridColumns: readonly GridColumn[],
): readonly GridColumn[] => {
  const sortedColumns = getSortedAndValidatedColumns(gridColumns);
  // Similar logic to `isExactlyEquallySizedDenseRow` above, keep in sync
  const numColumns = sortedColumns.length;
  const columnSize = NUM_ROW_SEGMENTS / numColumns;
  return produce(sortedColumns, (draft) => {
    draft.forEach((col, i) => {
      col.start = Math.round(columnSize * i);
      col.end = Math.round(columnSize * (i + 1));
    });
  });
};
